import type { ReactNode } from 'react';

import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';

/**
 * Loading, error, empty, content -- the four outcomes of asking the server for
 * something, decided in one place.
 *
 * Every screen that fetches has these four cases, and the ones that get written
 * by hand are the ones where the empty case quietly becomes an error case, or
 * where a failed request leaves a spinner turning forever. Handing the whole
 * decision to one component is how the shell stops that from being a per-screen
 * judgement call.
 *
 * The prop type is structural rather than React Query's `UseQueryResult` so the
 * component can be driven by anything with the same four facts -- which is what
 * makes it testable without a query client.
 */
export interface AsyncState<TData> {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: TData | undefined;
  readonly refetch?: (() => unknown) | undefined;
}

export function AsyncSection<TData>({
  state,
  children,
  loadingLabel,
  errorTitle,
  isEmpty,
  emptyTitle = 'Nada por aqui ainda',
  emptyDescription,
  emptyAction,
}: {
  state: AsyncState<TData>;
  children: (data: TData) => ReactNode;
  loadingLabel?: string;
  errorTitle?: string;
  /** What counts as empty for this data. Omit when empty is not a possibility. */
  isEmpty?: (data: TData) => boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}) {
  if (state.isPending) {
    return <LoadingState {...(loadingLabel !== undefined && { label: loadingLabel })} />;
  }

  if (state.isError || state.data === undefined) {
    return (
      <ErrorState
        error={state.error}
        {...(errorTitle !== undefined && { title: errorTitle })}
        {...(state.refetch !== undefined && {
          onRetry: () => {
            void state.refetch?.();
          },
        })}
      />
    );
  }

  if (isEmpty?.(state.data) === true) {
    return (
      <EmptyState
        title={emptyTitle}
        {...(emptyDescription !== undefined && { description: emptyDescription })}
        {...(emptyAction !== undefined && { action: emptyAction })}
      />
    );
  }

  return <>{children(state.data)}</>;
}
